import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, LoadingState } from './ui';
import { useAuth } from '../context/AuthContext';
import service from '../services/accountTwoFactorService';

const emptyDraft = { old_password: '', new_password: '', confirm_password: '', new_hint: '', recovery_email_address: '' };
const inputClass = 'mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500';

export default function AccountTwoFactorSettings({ account, authInProgress = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  const canManage = ['admin', 'manager'].includes(user?.role);
  const connected = ['agent', 'ai', 'monitoring'].includes(account.type) && account.status === 'on'
    && account.has_session === true && account.live_status !== 'offline' && !authInProgress;
  const editable = canManage && connected;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; generation.current += 1; };
  }, []);

  useEffect(() => {
    generation.current += 1;
    setOpen(false);
    setState(null);
    setDraft(emptyDraft);
    setCode('');
    setError('');
    setSuccess('');
    setUncertain(false);
  }, [account.id, editable]);

  const run = async (operation, request) => {
    if (inFlight.current || !editable) return;
    const version = generation.current;
    inFlight.current = true;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const next = await request();
      if (!mounted.current || version !== generation.current) return;
      if (String(next?.account_id) !== String(account.id) || typeof next?.has_password !== 'boolean'
        || typeof next?.has_recovery_email_address !== 'boolean') throw new Error('Invalid two-factor response');
      setState(next);
      setUncertain(false);
      setCode('');
      if (operation !== 'read') {
        setDraft(emptyDraft);
        if (next.recovery_email_address_code_info) {
          setSuccess(operation === 'resend' ? 'Код отправлен повторно.' : 'Подтвердите почту, чтобы завершить настройку пароля.');
        } else if (next.has_password) {
          setSuccess('Пароль двухэтапной аутентификации установлен в Telegram.');
        } else {
          setUncertain(true);
        }
      }
    } catch (failure) {
      if (!mounted.current || version !== generation.current) return;
      setError(failure?.response?.data?.message || 'Не удалось проверить результат. Обновите состояние из Telegram.');
      if (operation === 'read') setState(null);
      else {
        // Never retain passwords after a submission, including failed requests.
        setDraft(emptyDraft);
        setCode('');
        setUncertain(true);
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const refresh = () => run('read', () => service.get(account.id));
  const close = () => {
    if (inFlight.current) return;
    generation.current += 1;
    setOpen(false);
    setState(null);
    setDraft(emptyDraft);
    setCode('');
    setError('');
    setSuccess('');
    setUncertain(false);
  };
  const save = event => {
    event.preventDefault();
    if (!state || uncertain || busy || !editable || state.recovery_email_address_code_info) return;
    if (!draft.new_password || draft.new_password !== draft.confirm_password) {
      setError('Новый пароль и его повтор должны совпадать.');
      return;
    }
    if (state.has_password && !draft.old_password) { setError('Введите текущий пароль Telegram.'); return; }
    if (draft.new_hint && draft.new_hint === draft.new_password) { setError('Подсказка не должна совпадать с паролем.'); return; }
    const values = { old_password: draft.old_password, new_password: draft.new_password, new_hint: draft.new_hint };
    if (draft.recovery_email_address.trim()) values.recovery_email_address = draft.recovery_email_address.trim();
    run('password', () => service.setPassword(account.id, values));
  };
  const pending = state?.recovery_email_address_code_info;
  const disabled = busy || !editable || uncertain;
  const field = (name, label, options = {}) => (
    <label className="block text-sm font-medium text-gray-700">
      {label}
      <input className={inputClass} value={draft[name]} onChange={event => setDraft(value => ({ ...value, [name]: event.target.value }))}
        name={`two-factor-${name}`} maxLength={256} {...options} />
    </label>
  );

  return (
    <section className="mt-4 border-t border-gray-100 pt-4" aria-label="Двухэтапная аутентификация Telegram">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Двухэтапная аутентификация (2FA)</h3>
          <p className="mt-1 text-sm text-gray-500">Дополнительный пароль для входа в Telegram.</p>
        </div>
        {!open && <Button variant="secondary" size="sm" disabled={!editable || busy} onClick={() => { setOpen(true); refresh(); }}>Настроить 2FA</Button>}
      </div>
      {!canManage && <p className="mt-2 text-sm text-gray-500">Настройка доступна администратору и руководителю.</p>}
      {canManage && !connected && <p className="mt-2 text-sm text-gray-500">Включите аккаунт и завершите подключение Telegram.</p>}
      {open && <div className="mt-4 max-w-xl space-y-4">
        {busy && <LoadingState message="Проверяем состояние в Telegram…" />}
        {error && <div role="alert"><Alert variant="error">{error}</Alert></div>}
        {success && <div role="status"><Alert variant="success">{success}</Alert></div>}
        {uncertain && <Alert variant="warning">Обновите состояние перед следующей попыткой: предыдущий запрос мог примениться.</Alert>}
        {state && <p className="text-sm text-gray-700">{state.has_password ? 'Пароль 2FA включён.' : 'Пароль 2FA пока не установлен.'}{pending ? ' Ожидается подтверждение почты.' : ''}</p>}
        {pending ? (
          <form className="space-y-3" onSubmit={event => { event.preventDefault(); if (!disabled && code.trim()) run('confirm', () => service.confirmEmail(account.id, code.trim())); }}>
            <p className="text-sm text-gray-600">Код отправлен на {pending.email_address_pattern}. Изменение пароля завершится после подтверждения почты.</p>
            <label className="block text-sm font-medium text-gray-700">Код из письма
              <input className={inputClass} value={code} onChange={event => setCode(event.target.value)} disabled={disabled}
                autoComplete="one-time-code" inputMode="numeric" maxLength={32} required />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={disabled || !code.trim()}>Подтвердить почту</Button>
              <Button variant="secondary" disabled={disabled} onClick={() => run('resend', () => service.resendEmail(account.id))}>Отправить код повторно</Button>
            </div>
          </form>
        ) : state && (
          <form onSubmit={save} className="space-y-4">
            <fieldset disabled={disabled} className="space-y-3">
              {state.has_password && field('old_password', 'Текущий пароль Telegram', { type: 'password', autoComplete: 'current-password', required: true })}
              {state.has_password && state.password_hint && <p className="text-xs text-gray-500">Подсказка текущего пароля: {state.password_hint}</p>}
              {field('new_password', 'Новый пароль', { type: 'password', autoComplete: 'new-password', required: true })}
              {field('confirm_password', 'Повторите новый пароль', { type: 'password', autoComplete: 'new-password', required: true })}
              {field('new_hint', 'Подсказка (необязательно)', { type: 'text', autoComplete: 'off' })}
              {field('recovery_email_address', 'Почта восстановления (необязательно)', { type: 'email', autoComplete: 'email', maxLength: 254 })}
              <p className="text-xs text-gray-500">{state.has_recovery_email_address ? 'Почта восстановления уже настроена. Оставьте поле пустым, чтобы сохранить её.' : 'Рекомендуем указать почту: без неё восстановить забытый пароль будет сложнее.'}</p>
              <Button type="submit" disabled={disabled || !draft.new_password || !draft.confirm_password}>{state.has_password ? 'Сменить пароль 2FA' : 'Установить пароль 2FA'}</Button>
            </fieldset>
          </form>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" disabled={busy || !editable} onClick={refresh}>Обновить состояние 2FA</Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={close}>Закрыть настройки 2FA</Button>
        </div>
        <p className="text-xs text-gray-500">Сохраните пароль в надёжном месте. TeleFarm не сохраняет пароль в своей базе.</p>
      </div>}
    </section>
  );
}
